import { Box, Button, Grid, Modal, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { isNaN, isNil } from "lodash";
import { useSnackbar } from "notistack";
import React from "react";

import HarvestAPI from "../../api/Harvest.api";
import ReportAPI from "../../api/Report.api";
import CommodityCreatableAutocomplete from "../../components/CommodityCreatableAutocomplete";
import ShipperCreatableAutocomplete from "../../components/ShipperCreatableAutocomplete";
import { useCropRotationContext } from "../../contexts/CropRotation.context";
import { Commodity, CropRotation, Lot, Shipper } from "../../types";

export default function CropRotationBlock({
  cropRotation,
  lot,
}: {
  cropRotation: CropRotation;
  lot: Lot;
}) {
  const { cropWidth } = useCropRotationContext();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = React.useState<boolean>(false);

  const [commodity, setCommodity] = React.useState<Commodity | undefined>(
    cropRotation.commodity,
  );
  const [estHarvestDate, setEstHarvestDate] = React.useState<Dayjs | null>(
    cropRotation.estHarvestDate ? dayjs(cropRotation.estHarvestDate) : null,
  );
  const [projectedWetDate, setProjectedWetDate] = React.useState<Dayjs | null>(
    cropRotation.projectedWetDate ? dayjs(cropRotation.projectedWetDate) : null,
  );
  const [shipper, setShipper] = React.useState<Shipper | undefined>(
    cropRotation.shipper,
  );

  const { mutateAsync: updateHarvest } = HarvestAPI.useUpdate();

  const handleSave = async () => {
    try {
      if (isNil(projectedWetDate) && isNil(estHarvestDate)) {
        enqueueSnackbar(
          "Must select an estimated harvest date or a projected wet date",
          {
            variant: "error",
          },
        );
        return;
      }
      if (isNil(commodity)) {
        enqueueSnackbar("Must select a Commodity", {
          variant: "error",
        });
        return;
      }
      await updateHarvest({
        id: cropRotation.id,
        estHarvestDate: estHarvestDate
          ? estHarvestDate?.format("YYYY-MM-DD")
          : undefined,
        projectedWetDate: projectedWetDate
          ? projectedWetDate?.format("YYYY-MM-DD")
          : undefined,
        commodityId: commodity?.id,
        shipperId: shipper?.id,
      });
      setOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box
      key={cropRotation.id}
      sx={{
        width: cropWidth && `${cropWidth * 0.75}rem`,
        backgroundColor: `${commodity?.cropRotationColor}`,
        cursor: "pointer",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexGrow: 1,
          gap: "0.1rem",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography align="center" variant="body2">
            {commodity?.name}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography align="center" variant="body2">
            HD:{" "}
            {estHarvestDate ? dayjs(estHarvestDate).format("MM/DD/YYYY") : null}
          </Typography>
          <Typography align="center" variant="body2">
            WD:{" "}
            {projectedWetDate
              ? dayjs(projectedWetDate).format("MM/DD/YYYY")
              : null}
          </Typography>
        </Box>
      </Box>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DatePicker
                label="Projected Wet Date"
                value={projectedWetDate}
                onChange={(value) => setProjectedWetDate(value)}
                sx={{ width: "100%" }}
                slotProps={{
                  field: {
                    clearable: true,
                    onClear: () => setProjectedWetDate(null),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <DatePicker
                label="Est Harvest Date"
                value={estHarvestDate}
                onChange={(value) => setEstHarvestDate(value)}
                sx={{ width: "100%" }}
                slotProps={{
                  field: {
                    clearable: true,
                    onClear: () => setEstHarvestDate(null),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <CommodityCreatableAutocomplete
                setCurrentCommodity={(commodity: Commodity) =>
                  setCommodity(commodity)
                }
                commodity={commodity}
              />
            </Grid>
            <Grid item xs={12}>
              <ShipperCreatableAutocomplete
                setCurrentShipper={(shipper: Shipper) => setShipper(shipper)}
                shipper={shipper}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: "1rem",
            }}
          >
            <Button onClick={handleSave} variant="contained" fullWidth>
              Save
            </Button>
            <Button
              onClick={() => setOpen(false)}
              variant="contained"
              color="secondary"
              fullWidth
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
